import React, { useEffect, useState, useCallback } from "react";
import { fmtMoney } from "./money";
import { api, extractApiError } from "@/lib/api";
import { catalogueErrorMessage } from "@/lib/catalogueErrors";
import { courseLessonIds } from "../lib/courseRules";
import { pluralKey } from "../lib/pluralKey";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, GraduationCap } from "lucide-react";
// Relative paths on purpose (not "@/..."): these modules must be RESOLVED for real under
// Jest, not mocked; the "@/" alias is not mapped on the Jest side (see
// lib/LanguageContext.jsx). A relative path resolves natively.
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";
import CapabilityNotice from "./entitlements/CapabilityNotice";
import CourseEditor from "./courses/CourseEditor";

/**
 * LearnAdmin: the practice's courses (list, publish toggle, delete) and the door into
 * the course editor (./courses/CourseEditor.jsx), which owns lessons and quizzes.
 * Backend: /api/learn/courses
 */
export default function LearnAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [courses, setCourses] = useState([]);
  // null = the list; { course } = the editor, on a stored course or on a new one (null).
  const [editor, setEditor] = useState(null);
  // The server gates course AUTHORING (create / update / delete) on `learn.courses`;
  // reading the list stays open. Without the capability the page therefore stays a
  // read-only list of the courses existing students are taking, and every authoring
  // control gives way to the plan notice. `null` (map not known yet) claims nothing.
  const ent = useEntitlements();
  const coursesState = ent.status(CAP.courses);
  const canAuthor = coursesState !== "not_in_plan" && coursesState !== "coming_soon";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/learn/courses");
      setCourses(data);
    } catch (e) {
      toast.error(extractApiError(e, t("learn.error")));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm(t("learn.confirmDelete"))) return;
    try {
      await api.delete(`/learn/courses/${id}`);
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("learn.error")));
    }
  };

  const togglePublish = async (c) => {
    try {
      const payload = {
        title: c.title,
        description: c.description || "",
        cover_url: c.cover_url || "",
        price_cents: c.price_cents || 0,
        ...(c.currency ? { currency: c.currency } : {}),
        published: !c.published,
        tax_category: c.tax_category || "standard",
        exemption_citation: c.exemption_citation || "",
        sac_code: c.sac_code || "",
        lessons: c.lessons || [],
      };
      await api.put(`/learn/courses/${c.id}`, payload);
      toast.success(c.published ? t("learn.unpublishedToast") : t("learn.publishedToast"));
      load();
    } catch (e) {
      toast.error(catalogueErrorMessage(e, tc, t("learn.error")));
    }
  };

  if (editor) {
    return (
      <CourseEditor
        key={editor.course?.id || "new"}
        initial={editor.course}
        onSaved={() => { setEditor(null); load(); }}
        onCancel={() => setEditor(null)}
      />
    );
  }

  return (
    <div data-testid="learn-admin" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3"><GraduationCap className="w-6 h-6 text-sol-gold" /> {t("learn.title")}</h1>
          <p className="text-eh-muted text-sm mt-1">{courses.length} {t("learn.coursesWord")} · {t("learn.sellHint")}</p>
        </div>
        {canAuthor && (
          <Button onClick={() => setEditor({ course: null })} data-testid="learn-new" className="bg-eh-ink text-eh-bg">
            <Plus className="w-4 h-4 mr-1" /> {t("learn.newCourse")}
          </Button>
        )}
      </div>
      <CapabilityNotice
        state={coursesState}
        eta={ent.eta(CAP.courses)}
        body={t("learn.planNotice")}
        testId="learn-plan-notice"
      />
      <div className="grid lg:grid-cols-3 gap-4">
        {courses.length === 0 && <p className="col-span-full text-center text-eh-muted py-12">{t("learn.coursesEmpty")}</p>}
        {courses.map((c) => {
          const quizCount = (c.lessons || []).filter((l) => l.lesson_type === "quiz").length;
          // Two questions, two answers. What the STORED course holds is the server's one
          // predicate (`lib/courseRules`, a lesson counts when it has an id): it drives the
          // count and the published-but-empty warning, so the card says what the storefront
          // and Global Learn say about the same row, legacy id-less lessons included.
          // Whether the Publish toggle may be pressed is the AUTHORING rule, judged on what
          // the toggle sends — the stored lessons, each of which the server mints an id
          // for — so only a course with no lesson entry at all is blocked; one that is
          // ALREADY published and empty can only be unpublished, so that button stays enabled.
          const lessonCount = courseLessonIds(c).length;
          const publishBlocked = (c.lessons || []).length === 0 && !c.published;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-eh-border-soft p-5" data-testid={`course-card-${c.id}`}>
              {c.cover_url && <img src={c.cover_url} alt="" className="rounded-xl mb-3 aspect-video object-cover" />}
              <div className="flex gap-2 mb-2 flex-wrap">
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${c.published ? "bg-emerald-100 text-emerald-700" : "bg-eh-cream text-eh-muted"}`}>{c.published ? t("learn.published") : t("learn.draft")}</span>
                <span className="text-[10px] text-eh-muted" data-testid={`course-lessons-${c.id}`}>{tc(pluralKey(lang, lessonCount, "count.lessons"), { n: lessonCount })}</span>
                {quizCount > 0 && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{quizCount} quiz</span>}
              </div>
              {c.published && lessonCount === 0 && (
                <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-2.5 py-1.5 mb-2" data-testid={`course-empty-published-${c.id}`}>
                  {t("learn.publishedEmptyWarning")}
                </p>
              )}
              <h3 className="font-display text-lg mb-1">{c.title}</h3>
              <p className="text-sm text-eh-muted line-clamp-2 mb-3">{c.description || "—"}</p>
              {canAuthor && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditor({ course: c })} data-testid={`learn-edit-${c.id}`}>{t("learn.editBtn")}</Button>
                  <Button
                    size="sm"
                    variant={c.published ? "outline" : "default"}
                    className={c.published ? "text-amber-700 border-amber-300 hover:bg-amber-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    onClick={() => togglePublish(c)}
                    disabled={publishBlocked}
                    title={publishBlocked ? t("learn.publishNeedsLesson") : undefined}
                    data-testid={`learn-toggle-publish-${c.id}`}
                  >
                    {c.published ? t("learn.unpublishBtn") : t("learn.publishBtn")}
                  </Button>
                  <button onClick={() => del(c.id)} className="p-2 text-rose-500 hover:bg-rose-50 rounded ml-auto" data-testid={`learn-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
              <div className="text-sm text-eh-ink mt-3">
                {c.price_cents > 0 ? fmtMoney(c.price_cents, c.currency) : <span className="text-emerald-600">{t("learn.free")}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
